import { useMemo } from 'react';
import { useSiderMenuQuery } from '../services/permissions';

interface Permission {
  key?: string;
  permission?: string;
}

interface MenuItem {
  permissions?: Permission[];
  children?: MenuItem[];
}

interface SiderData {
  userType?: string;
  menu?: MenuItem[];
  permissions?: Permission[];
  userDetailMenu?: MenuItem[];
  employeeDetailMenu?: MenuItem[];
  generalStaffDetailMenu?: MenuItem[];
  trainerDetailMenu?: MenuItem[];
  directorDetailMenu?: MenuItem[];
}

// Walks a nested menu tree (items + their children) and adds every
// permission key/string it carries into the given sets.
const collectFromTree = (items: MenuItem[] | undefined, keys: Set<string>, strings: Set<string>) => {
  if (!items?.length) return;
  const walk = (nodes: MenuItem[]) => {
    nodes.forEach(node => {
      (node.permissions || []).forEach(p => {
        if (p.key)        keys.add(p.key);
        if (p.permission) strings.add(p.permission);
      });
      if (node.children?.length) walk(node.children);
    });
  };
  walk(items);
};

const usePermissions = () => {
  const { data: siderData } = useSiderMenuQuery(undefined) as { data?: SiderData };
  const isSuperAdmin = siderData?.userType === 'SUPERADMIN';

  // The backend spreads permissions across several shapes — a flat top-level
  // `permissions` list (carries tab/action keys like "9-ud-*" that aren't part
  // of any nav tree), the top-nav `menu` tree, and per-entity detail-page tab
  // trees (`userDetailMenu`, `trainerDetailMenu`, …). Union all of them so a
  // key is recognised no matter which shape the backend put it in.
  const { permKeys, permStrings } = useMemo(() => {
    if (!siderData) return { permKeys: null, permStrings: null };

    const keys = new Set<string>();
    const strings = new Set<string>();

    (siderData.permissions || []).forEach(p => {
      if (p.key)        keys.add(p.key);
      if (p.permission) strings.add(p.permission);
    });

    collectFromTree(siderData.menu, keys, strings);
    collectFromTree(siderData.userDetailMenu, keys, strings);
    collectFromTree(siderData.employeeDetailMenu, keys, strings);
    collectFromTree(siderData.generalStaffDetailMenu, keys, strings);
    collectFromTree(siderData.trainerDetailMenu, keys, strings);
    collectFromTree(siderData.directorDetailMenu, keys, strings);

    if (keys.size === 0 && strings.size === 0) return { permKeys: null, permStrings: null };
    return { permKeys: keys, permStrings: strings };
  }, [siderData]);

  const hasPermission = (keyOrPermission: string): boolean => {
    if (!keyOrPermission) return true;
    if (!permKeys)        return true;
    if (isSuperAdmin)     return true;
    return permKeys.has(keyOrPermission) || (permStrings?.has(keyOrPermission) ?? false);
  };

  return { hasPermission, permissionsLoaded: !!siderData, isSuperAdmin };
};

export default usePermissions;
